export interface SeasonalParticle {
  emoji?: string;
  shape?: 'circle' | 'heart' | 'leaf' | 'drop' | 'star';
  color: string;
  count: number;
  sizeRange: [number, number];
  animation: string;
  opacity: number;
}

export interface SeasonalConfig {
  particles: SeasonalParticle[];
  name: string;
}

export const SEASONAL_CONFIGS: Record<number, SeasonalConfig> = {
  0: {
    name: 'winter',
    particles: [
      { emoji: '❄️', color: '#E2E8F0', count: 12, sizeRange: [12, 18], animation: 'snowfall', opacity: 0.5 },
      { shape: 'circle', color: '#CBD5E1', count: 20, sizeRange: [3, 8], animation: 'snowfall', opacity: 0.6 },
      { shape: 'star', color: '#E0F2FE', count: 8, sizeRange: [4, 7], animation: 'sparkle', opacity: 0.4 },
    ],
  },
  1: {
    name: 'valentine',
    particles: [
      { shape: 'heart', color: '#FDA4AF', count: 10, sizeRange: [10, 16], animation: 'floatUp', opacity: 0.35 },
      { shape: 'heart', color: '#FB7185', count: 6, sizeRange: [6, 10], animation: 'floatUp', opacity: 0.25 },
      { shape: 'circle', color: '#FECDD3', count: 15, sizeRange: [2, 4], animation: 'sparkle', opacity: 0.4 },
    ],
  },
  2: {
    name: 'spring',
    particles: [
      { emoji: '🌸', color: '#FBCFE8', count: 8, sizeRange: [14, 20], animation: 'windyFall', opacity: 0.45 },
      { emoji: '🦋', color: '#A5B4FC', count: 4, sizeRange: [16, 20], animation: 'batFly', opacity: 0.4 },
      { shape: 'circle', color: '#BBF7D0', count: 12, sizeRange: [2, 5], animation: 'gentleFloat', opacity: 0.35 },
    ],
  },
  3: {
    name: 'rain',
    particles: [
      { shape: 'drop', color: '#93C5FD', count: 25, sizeRange: [2, 4], animation: 'rainfall', opacity: 0.35 },
      { shape: 'drop', color: '#BFDBFE', count: 15, sizeRange: [1, 3], animation: 'rainfall', opacity: 0.25 },
      { shape: 'circle', color: '#DBEAFE', count: 8, sizeRange: [3, 6], animation: 'shimmer', opacity: 0.3 },
    ],
  },
  4: {
    name: 'flowers',
    particles: [
      { emoji: '🌼', color: '#FDE047', count: 6, sizeRange: [16, 22], animation: 'gentleFloat', opacity: 0.45 },
      { emoji: '🌷', color: '#F472B6', count: 5, sizeRange: [14, 18], animation: 'gentleFloat', opacity: 0.4 },
      { emoji: '🦋', color: '#C4B5FD', count: 4, sizeRange: [14, 18], animation: 'batFly', opacity: 0.35 },
      { shape: 'circle', color: '#FEF08A', count: 10, sizeRange: [2, 4], animation: 'sparkle', opacity: 0.4 },
    ],
  },
  5: {
    name: 'summer',
    particles: [
      { shape: 'star', color: '#FCD34D', count: 12, sizeRange: [4, 8], animation: 'sparkle', opacity: 0.45 },
      { shape: 'circle', color: '#FEF3C7', count: 15, sizeRange: [2, 5], animation: 'shimmer', opacity: 0.35 },
      { emoji: '☀️', color: '#FBBF24', count: 3, sizeRange: [18, 24], animation: 'gentleFloat', opacity: 0.3 },
    ],
  },
  6: {
    name: 'celebration',
    particles: [
      { shape: 'star', color: '#F472B6', count: 10, sizeRange: [4, 8], animation: 'sparkle', opacity: 0.4 },
      { shape: 'star', color: '#60A5FA', count: 10, sizeRange: [4, 8], animation: 'sparkle', opacity: 0.4 },
      { shape: 'star', color: '#A78BFA', count: 8, sizeRange: [3, 6], animation: 'sparkle', opacity: 0.35 },
      { shape: 'circle', color: '#FDE68A', count: 12, sizeRange: [2, 4], animation: 'sparkle', opacity: 0.45 },
    ],
  },
  7: {
    name: 'warmth',
    particles: [
      { shape: 'circle', color: '#FDBA74', count: 12, sizeRange: [5, 10], animation: 'shimmer', opacity: 0.3 },
      { shape: 'circle', color: '#FED7AA', count: 15, sizeRange: [3, 6], animation: 'gentleFloat', opacity: 0.25 },
      { shape: 'star', color: '#FEF3C7', count: 8, sizeRange: [3, 5], animation: 'sparkle', opacity: 0.35 },
    ],
  },
  8: {
    name: 'autumn',
    particles: [
      { emoji: '🍂', color: '#F97316', count: 10, sizeRange: [16, 22], animation: 'leafFall', opacity: 0.45 },
      { emoji: '🍁', color: '#DC2626', count: 6, sizeRange: [14, 18], animation: 'leafFall', opacity: 0.4 },
      { shape: 'circle', color: '#FED7AA', count: 10, sizeRange: [2, 4], animation: 'windyFall', opacity: 0.3 },
    ],
  },
  9: {
    name: 'halloween',
    particles: [
      { emoji: '🦇', color: '#6B7280', count: 5, sizeRange: [18, 24], animation: 'batFly', opacity: 0.35 },
      { emoji: '🎃', color: '#F97316', count: 4, sizeRange: [16, 22], animation: 'gentleFloat', opacity: 0.35 },
      { emoji: '👻', color: '#F3F4F6', count: 4, sizeRange: [16, 20], animation: 'floatUp', opacity: 0.3 },
      { shape: 'circle', color: '#7C3AED', count: 10, sizeRange: [2, 4], animation: 'sparkle', opacity: 0.3 },
    ],
  },
  10: {
    name: 'lateAutumn',
    particles: [
      { emoji: '🍁', color: '#DC2626', count: 8, sizeRange: [16, 20], animation: 'leafFall', opacity: 0.4 },
      { emoji: '🍂', color: '#B45309', count: 8, sizeRange: [14, 18], animation: 'leafFall', opacity: 0.35 },
      { shape: 'leaf', color: '#92400E', count: 6, sizeRange: [10, 14], animation: 'windyFall', opacity: 0.3 },
      { shape: 'circle', color: '#D6D3D1', count: 12, sizeRange: [2, 4], animation: 'windyFall', opacity: 0.25 },
    ],
  },
  11: {
    name: 'holiday',
    particles: [
      { emoji: '❄️', color: '#BFDBFE', count: 15, sizeRange: [12, 18], animation: 'snowfall', opacity: 0.45 },
      { shape: 'circle', color: '#E0E7FF', count: 18, sizeRange: [3, 7], animation: 'snowfall', opacity: 0.5 },
      { shape: 'star', color: '#FEF3C7', count: 10, sizeRange: [4, 7], animation: 'sparkle', opacity: 0.4 },
      { emoji: '✨', color: '#FBBF24', count: 6, sizeRange: [10, 14], animation: 'sparkle', opacity: 0.35 },
    ],
  },
};

export const SEASONAL_ANIMATIONS = `
  @keyframes snowfall {
    0% { transform: translateY(-20px) rotate(0deg); }
    100% { transform: translateY(calc(100vh + 20px)) rotate(360deg); }
  }
  @keyframes rainfall {
    0% { transform: translateY(-20px); }
    100% { transform: translateY(calc(100vh + 20px)); }
  }
  @keyframes floatUp {
    0% { transform: translateY(100vh) scale(0.8); }
    50% { transform: translateY(50vh) scale(1); }
    100% { transform: translateY(-20px) scale(0.8); }
  }
  @keyframes windyFall {
    0% { transform: translate(0, -20px) rotate(0deg); }
    25% { transform: translate(30px, 25vh) rotate(90deg); }
    50% { transform: translate(-20px, 50vh) rotate(180deg); }
    75% { transform: translate(40px, 75vh) rotate(270deg); }
    100% { transform: translate(0, calc(100vh + 20px)) rotate(360deg); }
  }
  @keyframes leafFall {
    0% { transform: translate(0, -20px) rotate(0deg); }
    25% { transform: translate(20px, 25vh) rotate(45deg); }
    50% { transform: translate(-15px, 50vh) rotate(90deg); }
    75% { transform: translate(25px, 75vh) rotate(135deg); }
    100% { transform: translate(-10px, calc(100vh + 20px)) rotate(180deg); }
  }
  @keyframes gentleFloat {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    25% { transform: translateY(-15px) rotate(5deg); }
    50% { transform: translateY(-25px) rotate(-5deg); }
    75% { transform: translateY(-10px) rotate(3deg); }
  }
  @keyframes sparkle {
    0%, 100% { opacity: 0; transform: scale(0); }
    50% { opacity: 1; transform: scale(1); }
  }
  @keyframes shimmer {
    0%, 100% { opacity: 0.2; transform: scale(0.8); }
    50% { opacity: 0.5; transform: scale(1.2); }
  }
  @keyframes batFly {
    0% { transform: translate(0, 0) scaleX(1); }
    25% { transform: translate(50px, -30px) scaleX(1); }
    50% { transform: translate(100px, 0) scaleX(-1); }
    75% { transform: translate(50px, 30px) scaleX(-1); }
    100% { transform: translate(0, 0) scaleX(1); }
  }
`;
